import { Suspense, lazy, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshIcon } from 'assets/icons';
import Button from 'components/button';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import Tooltip from 'components/tooltips/Tooltips';

const InvoicePdfDocument = lazy(() => import('./InvoicePdfDocument'));

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const DEFAULT_ZOOM_STEP = 2;

const TOOL_CLASS_NAME =
  'w-8 h-8 flex items-center justify-center rounded-md text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[transparent]';

// An icon-only control: its label is both its accessible name and its tooltip.
const ToolButton = ({ label, children, ...props }) => (
  <Tooltip text={label}>
    <button
      type="button"
      className={TOOL_CLASS_NAME}
      aria-label={label}
      {...props}
    >
      {children}
    </button>
  </Tooltip>
);

const MagnifierIcon = ({ withPlus }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="11" cy="11" r="7" stroke="#374151" strokeWidth="2" />
    <path
      d="M21 21l-4.35-4.35M8 11h6"
      stroke="#374151"
      strokeWidth="2"
      strokeLinecap="round"
    />
    {withPlus && (
      <path
        d="M11 8v6"
        stroke="#374151"
        strokeWidth="2"
        strokeLinecap="round"
      />
    )}
  </svg>
);

// The message of each failure, and whether trying again can help.
const FAILURES = {
  busy: {
    key: 'busy',
    fallback: 'Un import est en cours, réessayez dans un instant.',
    canRetry: true,
  },
  notFound: {
    key: 'notFound',
    fallback: 'Le PDF de cette facture est introuvable.',
    canRetry: false,
  },
  besttFailed: {
    key: 'besttFailed',
    fallback: 'Bestt n’a pas rendu le PDF, réessayez dans un instant.',
    canRetry: true,
  },
  failed: {
    key: 'failed',
    fallback: 'L’aperçu n’a pas pu être chargé.',
    canRetry: true,
  },
};

const Centered = ({ children, role }) => (
  <div
    role={role}
    className="flex flex-col items-center justify-center gap-3 h-full py-16 px-6 text-center text-sm text-gray-600"
  >
    {children}
  </div>
);

/**
 * The left half of the invoice detail (JBWEB-1274): its PDF, with zoom,
 * rotation and pages. `pdf` comes from `useInvoicePdf`. `isLive` (a
 * discovered invoice, decision 42 of the api README: never inferred from
 * `pdfSource`) says the PDF was read from BeSTT just now, never stored.
 */
const InvoicePdfPreview = ({ pdf, isLive, text }) => {
  const previewText = (key, fallback) => text(`preview.${key}`, fallback);
  const [zoomStep, setZoomStep] = useState(DEFAULT_ZOOM_STEP);
  const [rotation, setRotation] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);

  const onLoadSuccess = ({ numPages }) => {
    setPageCount(numPages);
    setPageNumber(1);
  };

  let content;
  if (pdf.failure) {
    const failure = FAILURES[pdf.failure] ?? FAILURES.failed;
    content = (
      <Centered role="alert">
        <div>{previewText(failure.key, failure.fallback)}</div>
        {failure.canRetry && (
          <Button color="SECONDARY" onClick={pdf.reload}>
            {previewText('retry', 'Réessayer')}
          </Button>
        )}
      </Centered>
    );
  } else if (pdf.isLoading || !pdf.blob) {
    content = (
      <Centered role="status">
        {previewText('loading', 'Chargement du PDF…')}
      </Centered>
    );
  } else {
    content = (
      <div className="flex justify-center p-6 min-w-fit">
        <Suspense
          fallback={
            <Centered role="status">
              {previewText('loading', 'Chargement du PDF…')}
            </Centered>
          }
        >
          <InvoicePdfDocument
            file={pdf.blob}
            pageNumber={pageNumber}
            scale={ZOOM_STEPS[zoomStep]}
            rotation={rotation}
            onLoadSuccess={onLoadSuccess}
            onLoadError={pdf.markUnreadable}
          />
        </Suspense>
      </div>
    );
  }

  const hasDocument = Boolean(pdf.blob) && !pdf.failure;

  return (
    <div className="flex flex-col flex-1 min-w-0 min-h-0 bg-gray-50">
      {isLive && (
        <div
          role="note"
          className="flex-shrink-0 px-6 py-2 text-xs text-blue-800 bg-blue-50 border-b border-blue-100"
        >
          {previewText('live', 'Aperçu chargé en direct depuis Bestt')}
        </div>
      )}
      <div className="flex-shrink-0 flex items-center justify-end gap-1 px-4 py-2">
        {pageCount > 1 && (
          <div className="flex items-center gap-1 mr-auto text-sm text-gray-600">
            <ToolButton
              label={previewText('previousPage', 'Page précédente')}
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber((page) => page - 1)}
            >
              <ChevronLeft wh={18} color="#374151" />
            </ToolButton>
            <span>
              {fillText(previewText('pageOf', '{page} / {pages}'), {
                page: pageNumber,
                pages: pageCount,
              })}
            </span>
            <ToolButton
              label={previewText('nextPage', 'Page suivante')}
              disabled={pageNumber >= pageCount}
              onClick={() => setPageNumber((page) => page + 1)}
            >
              <ChevronRight wh={18} color="#374151" />
            </ToolButton>
          </div>
        )}
        <ToolButton
          label={previewText('rotate', 'Pivoter')}
          disabled={!hasDocument}
          onClick={() => setRotation((angle) => (angle + 90) % 360)}
        >
          <RefreshIcon wh={18} color="#374151" />
        </ToolButton>
        <ToolButton
          label={previewText('zoomOut', 'Zoom arrière')}
          disabled={!hasDocument || zoomStep === 0}
          onClick={() => setZoomStep((step) => step - 1)}
        >
          <MagnifierIcon />
        </ToolButton>
        <ToolButton
          label={previewText('zoomIn', 'Zoom avant')}
          disabled={!hasDocument || zoomStep === ZOOM_STEPS.length - 1}
          onClick={() => setZoomStep((step) => step + 1)}
        >
          <MagnifierIcon withPlus />
        </ToolButton>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">{content}</div>
    </div>
  );
};

export default InvoicePdfPreview;
